import { z } from 'zod';
import { newId } from './ids.js';
import { createPollInput, MAX_POLL_OPTIONS } from './poll.js';
import { phoneLegSchema } from './phone.js';
import { publicUserSchema } from './user.js';
import { MAX_FILES_PER_MESSAGE } from './file.js';
import { MAX_MESSAGE_LENGTH } from './message.js';
import { richTextDocSchema } from './richtext.js';
/**
 * A huddle szerzodese.
 *
 * A projekt egesz indoka az, hogy a Slack huddle-je megbizhatatlan lett:
 * nem indul, nem lehet kilepni, nem jelez. Ezert itt minden szabaly, amitol
 * ez nem fordulhat elo, KIMONDOTT es TESZTELHETO - nem a UI-kodban lapul.
 */
// ─── Allapotgep ───────────────────────────────────────────────────────────────
export const huddleStates = [
    'idle',
    'preflight',
    'connecting',
    'connected',
    'reconnecting',
    'leaving',
    'failed',
];
export const huddleStateSchema = z.enum(huddleStates);
/**
 * Az ERVENYES atmenetek.
 *
 * A legfontosabb szabaly: MINDEN allapotbol el lehet jutni az `idle`-be.
 * Ha ez serul, a felhasznalo beragad egy hivasba, amibol nem tud kilepni -
 * pontosan az a hiba, ami miatt ez a projekt elindult. A `huddle.test.ts`
 * ezt gepileg ellenorzi, nem emberi figyelemre bizzuk.
 */
export const HUDDLE_TRANSITIONS = {
    idle: ['preflight'],
    preflight: ['connecting', 'failed', 'idle'],
    connecting: ['connected', 'failed', 'leaving'],
    connected: ['reconnecting', 'leaving', 'failed'],
    reconnecting: ['connected', 'leaving', 'failed'],
    // A kilepes mindig sikerul: a `leaving` egyetlen kimenete az `idle`.
    leaving: ['idle'],
    failed: ['idle', 'preflight'],
};
export function canTransition(from, to) {
    return HUDDLE_TRANSITIONS[from].includes(to);
}
/** Bent van-e a felhasznalo media-szempontbol (ilyenkor kell a heartbeat). */
export function isLiveState(state) {
    return state === 'connected' || state === 'reconnecting';
}
// ─── Eloellenorzes ────────────────────────────────────────────────────────────
export const preflightCheckIds = ['microphone', 'devices', 'server'];
export const preflightCheckIdSchema = z.enum(preflightCheckIds);
export const preflightCheckSchema = z.object({
    id: preflightCheckIdSchema,
    ok: z.boolean(),
    /**
     * Ha nem ok: KONKRET, magyar nyelvu ok. Nem "hiba tortent", hanem
     * "a bongeszo letiltotta a mikrofont ezen az oldalon".
     */
    detail: z.string().nullable(),
});
export const preflightResultSchema = z.object({
    ok: z.boolean(),
    checks: z.array(preflightCheckSchema),
    inputDevices: z.array(z.object({ deviceId: z.string(), label: z.string() })),
});
// ─── Adatok ───────────────────────────────────────────────────────────────────
export const huddleParticipantStateSchema = z.enum(['invited', 'joined']);
export const huddleParticipantSchema = z.object({
    user: publicUserSchema,
    /**
     * `invited` = hivjuk, de meg nem lepett be.
     *
     * A felulet MASKENT mutatja a kettot - ez a vizualis visszaigazolas a
     * csatlakozasrol: a hivo latja, hogy elment a hivas, es azt is, amikor
     * a masik tenyleg belep.
     */
    state: huddleParticipantStateSchema,
    joinedAt: z.string(),
    invitedAt: z.string().nullable(),
    isMuted: z.boolean(),
    isCameraOn: z.boolean(),
    isSharingScreen: z.boolean(),
    /**
     * Szuneten van: bent van, de eppen nincs jelen.
     *
     * Ez NEM ugyanaz, mint a nemitas. A nemitott hallgat minket; a szuneteltetett
     * nem hall es nem hallatszik, es kozben akar egy masik hivasban is lehet.
     * A ketto osszemosasa pontosan azt a talalgatast hozna vissza, amit el
     * akarunk kerulni: "miert nem valaszol?"
     */
    isPaused: z.boolean(),
    /** Ki nemitotta el kivulrol. A visszakapcsolast csak az erintett vegezheti. */
    mutedBy: z.string().nullable(),
});
// ─── Vendeg (H2) ──────────────────────────────────────────────────────────────
/**
 * Mit tehet a VENDEG a hivasban. A link letrehozasakor allithato (alapbol
 * mind), es hivas kozben is bovitheto vagy szukitheto.
 */
export const guestPermissionSchema = z.enum(['audio', 'camera', 'screen', 'chat']);
export const ALL_GUEST_PERMISSIONS = ['audio', 'camera', 'screen', 'chat'];
export const GUEST_PERMISSION_LABELS = {
    audio: 'Hang',
    camera: 'Kamera',
    screen: 'Képernyőmegosztás',
    chat: 'Chat',
};
/** A vendeg LiveKit-identitasanak elotagja - igy kulonbozik a kollegatol es a telefontol. */
export const GUEST_IDENTITY_PREFIX = 'guest_';
export const huddleGuestStateSchema = z.enum([
    'waiting',
    'admitted',
    'rejected',
    'left',
    'cancelled',
]);
/** A vendeg, ahogy a hivasban ulo kollegak latjak. */
export const huddleGuestSchema = z.object({
    id: z.string(),
    /** LiveKit-identitas: `guest_<id>`. */
    identity: z.string(),
    name: z.string(),
    state: huddleGuestStateSchema,
    permissions: z.array(guestPermissionSchema),
    requestedAt: z.string(),
    joinedAt: z.string().nullable(),
    decidedBy: z.string().nullable(),
});
// ─── Kiscsoportos szobak (docs/19) ────────────────────────────────────────────
/** Egy tag kulcsa a beosztasban: a felhasznalo azonositoja, vendegnel az identitasa (`guest_<id>`). */
export const breakoutModeSchema = z.enum(['random', 'manual']);
/** Az idoszak hossza percben: legalabb egy, legfeljebb egy ora. */
export const BREAKOUT_MIN_MINUTES = 1;
export const BREAKOUT_MAX_MINUTES = 60;
export const BREAKOUT_DEFAULT_MINUTES = 15;
export const BREAKOUT_DEFAULT_GROUP_SIZE = 2;
/** Az utolso ennyi masodpercben figyelmeztet a felulet. */
export const BREAKOUT_WARN_SECONDS = 60;
/**
 * A NYITOTT kiscsoportos idoszak, ahogy mindenki latja.
 *
 * A huddle pillanatkepenek resze - nem kulon esemeny -, hogy egy elveszett
 * keret ne hagyjon tartos elteres: a kovetkezo pillanatkep mindent helyrehoz.
 * A felulet ebbol szuri a hangot (csak a sajat csoport), es ebbol szamol vissza.
 */
export const huddleBreakoutSchema = z.object({
    id: z.string(),
    mode: breakoutModeSchema,
    startedAt: z.string(),
    /** Abszolut lejarat (ISO). A visszaszamlalas ebbol megy, nem a kuldes ota eltelt idobol. */
    endsAt: z.string(),
    /**
     * A szerver ideje a pillanatkep kesziteskor (ISO). Az ora-elteres kiszamitasahoz kell:
     * a vendegnek nincs WebSocketje, es egy rosszul allitott gep ora sem hamisithatja a vegét.
     */
    serverNow: z.string(),
    /** Tag-kulcs -> csoport sorszama (1-tol). Aki nincs benne (telefon, kesve jott), a foszobaban van. */
    assignments: z.record(z.string(), z.number().int().min(1)),
});
export const startBreakoutInputSchema = z
    .object({
    mode: breakoutModeSchema,
    durationMinutes: z.number().int().min(BREAKOUT_MIN_MINUTES).max(BREAKOUT_MAX_MINUTES),
    /** Veletlen beosztasnal: hany fos csoportok legyenek. */
    groupSize: z.number().int().min(2).max(50).optional(),
    /** Kezi beosztasnal: tag-kulcs -> csoport sorszama. */
    assignments: z.record(z.string(), z.number().int().min(1).max(50)).optional(),
})
    .refine((v) => (v.mode === 'random' ? v.groupSize !== undefined : v.assignments !== undefined), {
    message: 'Véletlen beosztáshoz csoportméret, kézihez beosztás kell.',
});
/** Hosszabbitas es/vagy egy ember athelyezese (a hazigazda kuldi). */
export const updateBreakoutInputSchema = z.object({
    extendMinutes: z.number().int().min(1).max(30).optional(),
    move: z
        .object({ memberKey: z.string().min(1), groupNo: z.number().int().min(1).max(50) })
        .optional(),
});
/** Hazigazda-atadas. */
export const setHostInputSchema = z.object({ userId: z.string().min(1) });
/**
 * Melyik csoportban van a tag? 0 = a FOSZOBABAN (nincs beosztva: a telefonos hivo,
 * a kesve csatlakozo). Nyitott idoszak nelkul mindenki ugyanabban a "csoportban" van.
 */
export function breakoutGroupOf(breakout, key) {
    return breakout?.assignments[key] ?? 0;
}
/** Halljak/latjak-e egymast? Igen, ha nincs idoszak, vagy ugyanabban a csoportban vannak. */
export function sameBreakoutGroup(breakout, a, b) {
    if (!breakout)
        return true;
    return breakoutGroupOf(breakout, a) === breakoutGroupOf(breakout, b);
}
/**
 * A HAZIGAZDA: az `hostId`, ennek hianyaban az indito - de csak ha bent van.
 * Ha o sem, a legregebben bent levo. Tiszta fuggveny: a szerver pillanatkepe es a
 * jogosultsag-ellenorzes UGYANEZT hasznalja, igy nem lehet elteres koztuk.
 */
export function effectiveHostId(hostId, startedBy, participants) {
    const joined = participants.filter((p) => p.state === 'joined');
    const preferred = hostId ?? startedBy;
    if (preferred && joined.some((p) => p.user.id === preferred))
        return preferred;
    const oldest = [...joined].sort((a, b) => a.joinedAt.localeCompare(b.joinedAt))[0];
    return oldest?.user.id ?? null;
}
/**
 * A veletlen beosztas: megkeverjuk, majd `size` fos csoportokra vagjuk.
 * Ha egyetlen ember maradna a vegen (es van mar csoport), a legutolso csoporthoz
 * csatoljuk - egyedul kesobb senki nem beszelgetne.
 */
export function planRandomGroups(keys, size, random = Math.random) {
    const shuffled = [...keys];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
        const j = Math.floor(random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    const result = {};
    const perGroup = Math.max(1, Math.floor(size));
    let groupNo = 0;
    shuffled.forEach((key, index) => {
        if (index % perGroup === 0)
            groupNo += 1;
        result[key] = groupNo;
    });
    // Az egyedul maradt utolso ember a megelozo csoportba kerul.
    if (groupNo > 1) {
        const lastKeys = Object.keys(result).filter((key) => result[key] === groupNo);
        if (lastKeys.length === 1)
            result[lastKeys[0]] = groupNo - 1;
    }
    return result;
}
// ─── Stopper / idozito az olelesben ───────────────────────────────────────────
export const huddleTimerModeSchema = z.enum(['stopwatch', 'countdown']);
/** Az idozito leghosszabb ideje: tiz ora. */
export const TIMER_MAX_SECONDS = 10 * 60 * 60;
/** A lejart idozito riado-ablaka: ennyi ideig jelenik meg a nagy ora (a kesve belepo ne lassa regi riasztast). */
export const TIMER_ALARM_WINDOW_MS = 60_000;
/**
 * A KOZOS stopper/idozito allapota - a futas LEIRASA, nem a kijelzett ido.
 *
 * A kijelzett idot mindenki maga szamolja: `accumulatedMs` (az eddigi szakaszokbol)
 * + a jelenlegi szakasz (`runningSince`-tol a szerver mostani idejeig). Igy a ketszeres
 * kattintas, a lassu halo es a szerver-ujrainditas sem csuszik el, es egy kesve belepo
 * is a helyes erteket latja.
 */
export const storedTimerSchema = z.object({
    /** Minden inditas es nullazas uj azonositot kap: ehhez kotjuk a riasztas "elintezve" jelet. */
    id: z.string(),
    mode: huddleTimerModeSchema,
    running: z.boolean(),
    /** A korabbi (szuneteltetett) szakaszok osszege. */
    accumulatedMs: z.number().int().min(0),
    /** A jelenlegi szakasz kezdete (ISO); `null`, ha all. */
    runningSince: z.string().nullable(),
    /** Visszaszamlalasnal a teljes hossz; stopperen `null`. */
    durationMs: z.number().int().min(1000).nullable(),
});
/** A pillanatkepben a szerver idejevel egyutt megy ki (az ora-elteres kiszamitasahoz). */
export const huddleTimerSchema = storedTimerSchema.extend({ serverNow: z.string() });
export const timerActionInputSchema = z
    .discriminatedUnion('action', [
    z.object({
        action: z.literal('start'),
        mode: huddleTimerModeSchema,
        /** Visszaszamlalasnal kotelezo. */
        durationSeconds: z.number().int().min(1).max(TIMER_MAX_SECONDS).optional(),
    }),
    z.object({ action: z.literal('pause') }),
    z.object({ action: z.literal('resume') }),
    z.object({ action: z.literal('reset') }),
    z.object({ action: z.literal('clear') }),
])
    .refine((v) => v.action !== 'start' || v.mode === 'stopwatch' || v.durationSeconds !== undefined, {
    message: 'Az időzítőhöz idő kell.',
});
/** A hazigazda ki-be kapcsolja, hogy barki kezelheti-e. */
export const timerAccessInputSchema = z.object({ open: z.boolean() });
/** Mennyi ido telt el (ms), a megadott PILLANATBAN (a szerver oraja szerint). */
export function timerElapsedMs(timer, nowMs) {
    if (!timer.running || !timer.runningSince)
        return timer.accumulatedMs;
    return timer.accumulatedMs + Math.max(0, nowMs - Date.parse(timer.runningSince));
}
/** Visszaszamlalasnal a hatralevo ido (ms, legalabb 0); stopperen `null`. */
export function timerRemainingMs(timer, nowMs) {
    if (timer.mode !== 'countdown' || timer.durationMs === null)
        return null;
    return Math.max(0, timer.durationMs - timerElapsedMs(timer, nowMs));
}
/**
 * Mikor jart le (ms-epoch) a futo visszaszamlalas - vagy `null`, ha nem fut / stopper.
 * A riasztas ebbol tudja, mennyire friss.
 */
export function timerFinishesAtMs(timer) {
    if (timer.mode !== 'countdown' || timer.durationMs === null)
        return null;
    if (!timer.running || !timer.runningSince)
        return null;
    return Date.parse(timer.runningSince) + (timer.durationMs - timer.accumulatedMs);
}
/**
 * Az allapotgep: a kovetkezo allapot egy muveletbol. TISZTA fuggveny - a szerver
 * ezt futtatja, a tesztek ezt vizsgaljak.
 *
 *  - `start`: uj stopper/idozito, azonnal indul;
 *  - `pause` / `resume`: az eddigi ido megmarad; a lejart idozito nem folytathato (nullazni kell);
 *  - `reset`: nulla, all (visszaszamlalasnal ugyanaz a hossz, hogy ujra indithato legyen);
 *  - `clear`: eltunik a fejlecbol.
 */
export function nextTimer(previous, action, nowMs, makeId = newId) {
    const nowIso = new Date(nowMs).toISOString();
    switch (action.action) {
        case 'start':
            return {
                id: makeId(),
                mode: action.mode,
                running: true,
                accumulatedMs: 0,
                runningSince: nowIso,
                durationMs: action.mode === 'countdown' ? (action.durationSeconds ?? 1) * 1000 : null,
            };
        case 'clear':
            return null;
        case 'reset':
            return previous
                ? { ...previous, id: makeId(), running: false, accumulatedMs: 0, runningSince: null }
                : null;
        case 'pause':
            if (!previous || !previous.running)
                return previous;
            return {
                ...previous,
                running: false,
                accumulatedMs: timerElapsedMs(previous, nowMs),
                runningSince: null,
            };
        case 'resume': {
            if (!previous || previous.running)
                return previous;
            const finished = previous.durationMs !== null && previous.accumulatedMs >= previous.durationMs;
            if (finished)
                return previous;
            return { ...previous, running: true, runningSince: nowIso };
        }
    }
}
/** Kezelheti-e a stoppert: a hazigazda mindig, a tobbiek csak ha a hazigazda megnyitotta. */
export function canControlTimer(hostId, timerOpen, userId) {
    return hostId === userId || timerOpen;
}
// ─── Szavazas az olelesben ────────────────────────────────────────────────────
/** A hivasban inditott szavazas: ugyanazok a mezok, mint a csatornaszavazasnal, hatarido nelkul. */
export const createHuddlePollInput = createPollInput.omit({ closesAt: true });
/** A NYITOTT szavazas, ahogy a hivasban mindenki latja (elo megoszlassal). */
export const huddlePollSchema = z.object({
    id: z.string(),
    createdBy: z.string().nullable(),
    question: z.string(),
    multiple: z.boolean(),
    anonymous: z.boolean(),
    createdAt: z.string(),
    /** Hany KULONBOZO ember szavazott. */
    voterCount: z.number().int(),
    options: z
        .array(z.object({
        id: z.string(),
        text: z.string(),
        votes: z.number().int(),
        /** Kik szavaztak ra - nevtelen szavazasnal mindig ures. */
        voterIds: z.array(z.string()),
    }))
        .max(MAX_POLL_OPTIONS),
});
/** A lezart szavazas eredmenye - ez kerul a csatorna folyamaba (`meta.result`). */
export const huddlePollResultSchema = z.object({
    question: z.string(),
    anonymous: z.boolean(),
    multiple: z.boolean(),
    voterCount: z.number().int(),
    options: z.array(z.object({ text: z.string(), votes: z.number().int() })),
});
/** Egy opcio aranya a szavazok kozott (%), kerekitve. Tobbvalasztosnal az osszeg lehet >100. */
export function pollSharePercent(votes, voterCount) {
    return voterCount > 0 ? Math.round((votes / voterCount) * 100) : 0;
}
/** A gyoztes(ek) szovegei: a legtobb szavazatot kapottak (legalabb egy szavazattal). */
export function pollWinners(result) {
    const best = Math.max(0, ...result.options.map((option) => option.votes));
    return best > 0 ? result.options.filter((o) => o.votes === best).map((o) => o.text) : [];
}
/** Az eredmeny egyszeru szovege - a kereseshez, ertesiteshez es a nem kartyas nezetekhez. */
export function huddlePollResultText(result) {
    const lines = result.options.map((option) => `• ${option.text} — ${option.votes} szavazat (${pollSharePercent(option.votes, result.voterCount)}%)`);
    const winners = pollWinners(result);
    const tail = result.voterCount === 0
        ? 'Senki nem szavazott.'
        : `${result.voterCount} fő szavazott. ${winners.length === 1 ? 'Győztes' : 'Holtverseny'}: ${winners.join(', ')}.`;
    return [`📊 Szavazás az ölelésben: ${result.question}`, ...lines, tail].join('\n');
}
export const voteHuddlePollInput = z.object({
    /** A valasztott opciok. Ures lista: a szavazat visszavonasa. */
    optionIds: z.array(z.string()).max(MAX_POLL_OPTIONS),
});
export const huddleSchema = z.object({
    id: z.string(),
    channelId: z.string(),
    room: z.string(),
    title: z.string().nullable(),
    startedBy: z.string().nullable(),
    /**
     * A HAZIGAZDA (szamolt ertek, lasd `effectiveHostId`): csak o inditja es zarja a
     * kiscsoportos idoszakot. `null` = nincs bent senki.
     */
    hostId: z.string().nullable().default(null),
    /** A NYITOTT kiscsoportos idoszak, vagy `null`. */
    breakout: huddleBreakoutSchema.nullable().default(null),
    /** A KOZOS stopper/idozito (a fejlecben), vagy `null`. */
    timer: huddleTimerSchema.nullable().default(null),
    /** A hazigazdan kivul BARKI kezelheti-e a stoppert. */
    timerOpen: z.boolean().default(false),
    /** A NYITOTT szavazas az olelesben (elo megoszlassal), vagy `null`. */
    poll: huddlePollSchema.nullable().default(null),
    startedAt: z.string(),
    /** A csatornaba kiirt rendszeruzenet - erre lehet ugrani a folyamban. */
    messageId: z.string().nullable(),
    /**
     * Keszul-e atirat errol a hivasrol.
     *
     * A huddle EGESZ allapotaval egyutt megy ki, hogy MINDENKI lassa - nem
     * elrejtheto beallitas. Aki bent ul egy hivasban, annak joga van tudni, hogy
     * felvetel keszul rola; ez nem UI-kenyelem, hanem az egesz funkcio feltetele.
     */
    transcribe: z.boolean(),
    /**
     * TESZTHIVAS: eddig marad meg a hang (ISO), kulonben `null`.
     *
     * Ugyanazert megy ki mindenkinek, amiert a `transcribe`: aki bent ul, annak
     * tudnia kell, ha a hangja megmarad. Csak atirat-meresre valo.
     */
    audioKeepUntil: z.string().nullable().default(null),
    participants: z.array(huddleParticipantSchema),
    /**
     * A hivas TELEFONOS agai (SIP). Kulon lista, nem resztvevo: a telefonos fel
     * nem felhasznalo, es nem is szabad annak latszania.
     */
    phoneLegs: z.array(phoneLegSchema).default([]),
    /**
     * VENDEGEK (H2): a varakozok es a bent levok. A varakozot barki a hivasban
     * beengedheti; a kilepett vagy elutasitott mar nincs a listaban.
     */
    guests: z.array(huddleGuestSchema).default([]),
});
/**
 * Egy ICE-szerver, ahogy a bongeszo varja.
 *
 * A hosztnevet SZANDEKOSAN nevkent adjuk at, nem cimkent: a megosztott DNS
 * belulrol a LAN-cimre, kivulrol a publikus cimre oldja fel - igy ugyanaz a
 * beallitas szolgalja ki mindket kozonseget.
 */
export const iceServerSchema = z.object({
    urls: z.array(z.string()),
    username: z.string().optional(),
    credential: z.string().optional(),
});
export const huddleTokenSchema = z.object({
    /** A LiveKit szerver cime (ws:// vagy wss://). */
    url: z.string(),
    /** Rovid eletu, CSAK erre a szobara szolo JWT. */
    token: z.string(),
    room: z.string(),
    identity: z.string(),
    expiresAt: z.string(),
    /**
     * A hivashoz hasznalando ICE-szerverek.
     *
     * URES lista = nincs sajat TURN beallitva; ilyenkor a kliens a LiveKit sajat
     * listajat hasznalja, vagyis minden marad a regiben. Egy felig beallitott
     * TURN nem ronthatja el azt, ami eddig mukodott.
     */
    iceServers: z.array(iceServerSchema),
});
// ─── Bemenetek ────────────────────────────────────────────────────────────────
export const startHuddleInput = z.object({
    title: z.string().trim().max(120).optional(),
    /**
     * A ful azonositoja. Ettol mukodik a "masik fulon atveszem": ha ugyanaz a
     * felhasznalo mas `sessionId`-vel csatlakozik, a regi sessiont kileptetjuk.
     */
    sessionId: z.string().min(1).max(64),
    /**
     * FELVETEL: a bejovo hivas "Felveszem" gombja kuldi. Ha kozben egy kolléga
     * mar felvette (csatornara iranyitott telefonvonalnal tobben csengenek), a
     * szerver elutasitja - kulonben mindenki, aki megnyomta, bent kotne ki.
     */
    answer: z.boolean().optional(),
    /**
     * CSENDES inditas: a hivast egy KIMENO telefonhivashoz nyitjuk (visszahivas,
     * tarcsazo). Ilyenkor senkit nem csengetunk es nem ertesitunk - egy DM-bol
     * inditott visszahivas kulonben a DM masik felet is behivna, es harman
     * kotnenek ki egy hivasban, amit csak az egyikuk akart.
     */
    quiet: z.boolean().optional(),
});
export const huddleHeartbeatInput = z.object({
    sessionId: z.string().min(1).max(64),
    isMuted: z.boolean().optional(),
    isCameraOn: z.boolean().optional(),
    isSharingScreen: z.boolean().optional(),
    isPaused: z.boolean().optional(),
});
export const huddleInviteInput = z.object({
    userIds: z.array(z.string().min(1)).min(1).max(20),
});
/** Egy meg fel nem vett behivas visszavonasa: kinek a behivasat. */
export const huddleCancelInviteInput = z.object({ userId: z.string().min(1) });
export const huddleMuteInput = z.object({ userId: z.string().min(1) });
// ─── Oleles-chat ──────────────────────────────────────────────────────────────
/**
 * Az olelesben valtott uzenetek.
 *
 * **Ezek RENDES csatorna-uzenetek**, csak meg van jelolve rajtuk, hogy hivasbol
 * jottek (`meta.huddleId`). Ket okbol:
 *
 *  1. **A hivas vege nem torol.** Ami elhangzott - egy cim, egy hatarido, egy
 *     dontes -, az ott marad a csatornaban (DM-olelesnel a DM-ben), ahol
 *     keresheto es visszakeresheto. Egy kulon, hivas-elettartamu chat pont a
 *     fontos dolgokat veszitene el.
 *  2. **A kint levok is latjak.** Nem a hivas vegen, egyben - AZONNAL. Aki nem
 *     tudott bemenni, igy is kovetheti, ha valami fontos hangzik el.
 *
 * A hivas ablaka csak EZEKET kerdezi le, a csatorna tobbi uzenetet nem: a
 * behivott vendeg igy sem lat bele a csatorna elozmenyebe.
 */
/** Ennyi uzenetet mutat a hivas chat-panelje. Egy hivas nem archivum. */
export const HUDDLE_CHAT_LIMIT = 200;
/**
 * Uzenet a hivas chatjebol - UGYANAZ a tartalom, mint egy csatorna-uzenet.
 *
 * Kezdetben csak sima szoveg volt, 2000 jelig. Csakhogy ez az uzenet a
 * CSATORNABA kerul: ha a hivasban egy kepernyokepet, egy formazott listat vagy
 * egy GIF-et nem lehet elkuldeni, akkor a hivas kozben mindenki atmegy a fo
 * ablakba irni - es pont az a beszelgetes esik szet, amiert a chat itt van.
 *
 * A korlatokat (hossz, dokumentum-meret, fajlok szama) ugyanaz a szerver-
 * oldali ellenorzes vegzi, mint a csatornaban (`prepareBody`).
 */
export const huddleChatInput = z
    .object({
    text: z.string().max(MAX_MESSAGE_LENGTH),
    /** Idempotencia: ugyanaz a kuldes ketszer sem hoz letre ket uzenetet. */
    clientMsgId: z.string().min(1).max(64),
    body: richTextDocSchema.optional(),
    fileIds: z.array(z.string()).max(MAX_FILES_PER_MESSAGE).optional(),
    isImportant: z.boolean().optional(),
})
    .refine((value) => value.text.trim().length > 0 || (value.fileIds?.length ?? 0) > 0, {
    message: 'Az üzenet nem lehet üres.',
});
export const huddleEventKinds = [
    'preflight_failed',
    'join_attempt',
    'join_success',
    'join_failed',
    /**
     * A felhasznalo maga hagyta abba a csatlakozast (bontott, bezarta az ablakot,
     * masik ful vette at), mielott bejutott volna. Nem kudarc: a sikeraranyba
     * nem szamit bele.
     */
    'join_cancelled',
    'reconnecting',
    'reconnected',
    'dropped',
    'left',
];
export const huddleEventInput = z.object({
    kind: z.enum(huddleEventKinds),
    detail: z.string().max(500).optional(),
    durationMs: z.number().int().nonnegative().max(600_000).optional(),
});
// ─── Idozitesek ───────────────────────────────────────────────────────────────
/**
 * Ilyen surun jelez a kliens, hogy meg bent van.
 *
 * A takarito ennek a haromszorosat varja - egy kihagyott eletjel (halozati
 * pillanatnyi zavar) meg ne leptessen ki senkit.
 */
export const HUDDLE_HEARTBEAT_INTERVAL_MS = 20_000;
/** Ennyi eletjel nelkuli ido utan szellemnek szamit a resztvevo. */
export const HUDDLE_STALE_AFTER_MS = 60_000;
/** A LiveKit token elettartama. Rovid: csak a csatlakozashoz kell. */
export const HUDDLE_TOKEN_TTL_SECONDS = 300;
/** Egy huddle-be legfeljebb ennyien. */
export const HUDDLE_MAX_PARTICIPANTS = 50;
/**
 * A kliensek kozotti egyeztetes csatornaja (BroadcastChannel).
 *
 * Egy felhasznalonak egyszerre EGY ELO hivasa lehet: aki csatlakozik, "igenyt
 * jelent be", es a tobbi ablak bezar. A SZUNETELTETETT hivasok kivetelek -
 * azok nem szolnak es nem hallatszanak, tehat nyugodtan futhat beloluk tobb
 * is (eppen ez a szunet ertelme: "atmegyek egy masik megbeszelesre").
 */
export const HUDDLE_BROADCAST_CHANNEL = 'avslack-huddle';
// ─── Kulon ablak ──────────────────────────────────────────────────────────────
/**
 * A huddle SAJAT ablakban fut.
 *
 * Ket oka van, es mindketto gyakorlati:
 *  - **Nagyobb felulet**: a resztvevok nagy profilkeppel/kameraval, a
 *    megosztott kepernyo olvashato mereten - ez egy oldalsavban nem fer el.
 *  - **Tulel mindent**: a fo ablak bezarasa, ujratoltese vagy egy elgepelt
 *    navigacio nem szakitja meg a hivast, mert a kapcsolatot a kulon ablak
 *    tartja.
 */
export const huddleWindowFeatures = (width = 1100, height = 760) => `popup=yes,width=${width},height=${height},menubar=no,toolbar=no,location=no,status=no`;
export const huddleWindowName = (huddleId) => `avslack-huddle-${huddleId}`;
// ─── Hivas kozbeni reakciok ───────────────────────────────────────────────────
/**
 * Reakciok huddle kozben.
 *
 * Szandekosan SZUK a keszlet: egy hivas kozben nem bongeszni akar az ember,
 * hanem egy mozdulattal jelezni. Ezek a LiveKit adatcsatornajan mennek, nem
 * az adatbazison keresztul - mulandó jelzesek, nem uzenetek.
 */
export const huddleReactions = ['👍', '👏', '😂', '🎉', '❤️', '😮', '🤔', '🙏'];
/** Ennyi ideig latszik egy reakcio. */
export const HUDDLE_REACTION_TTL_MS = 4000;
/**
 * GYORSSZAVAZAS: egy szobeli kerdesre pipa (egyetert) vagy X (nem ert egyet).
 * Nincs letrehozasa, nincs kerdese - a gombok mindig ott vannak.
 */
export const quickVoteChoices = ['yes', 'no'];
/**
 * A gyorsszavazat a resztvevo LiveKit-adataban el ezen a kulcson. A kollegak
 * maguk irjak; a vendeg helyett a szerver (lasd guests/routes.ts).
 */
export const QUICK_VOTE_ATTRIBUTE = 'avslack.vote';
/** Ennyi ido utan, ha senki nem szavaz, a gyorsszavazas magatol eltunik. */
export const QUICK_VOTE_IDLE_MS = 2 * 60 * 1000;
// ─── Adatcsatorna ─────────────────────────────────────────────────────────────
/**
 * A hivas kozbeni, mulandó adatok szerzodese.
 *
 * Ezek NEM mennek at a szerveren: a LiveKit adatcsatornajan utaznak
 * resztvevotol resztvevoig. Ami elveszik, az elveszett - egy rajzvonal vagy
 * egy reakcio nem er annyit, hogy garantalt kezbesitest epitsunk ra.
 */
export const huddleDataFrameSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('reaction'),
        emoji: z.string().max(8),
        from: z.string(),
    }),
    z.object({
        type: z.literal('draw'),
        /** Melyik megosztott kepernyore rajzolunk. */
        target: z.string(),
        color: z.string().max(24),
        /** 0-1 kozotti relativ pontok - igy minden kijelzon ugyanoda kerul. */
        points: z.array(z.object({ x: z.number(), y: z.number() })).max(400),
        from: z.string(),
    }),
    z.object({ type: z.literal('draw.clear'), target: z.string(), from: z.string() }),
    /**
     * "Uj chat-uzenet erkezett" - PUSZTA JELZES, tartalom nelkul.
     *
     * A szoveget SOSEM innen vesszuk: a jelzes a mediabol jon, az adat a
     * szerverbol. Igy egy resztvevo nem irathat a panelbe mas neveben, es nem
     * szamit, ha egy keret elveszik - a kovetkezo lekerdezes ugyis behozza.
     */
    z.object({ type: z.literal('chat'), from: z.string() }),
    /**
     * GYORSSZAVAZAS NULLAZASA: barki kuldheti, es mindenki a SAJAT szavazatat
     * torli (a szavazat a resztvevo adataban el, azt csak o maga irhatja at).
     */
    z.object({ type: z.literal('vote.clear'), from: z.string() }),
    /**
     * SEGITSEGKERES a kiscsoportbol (docs/19): csak a hazigazdanak megy
     * (`destinationIdentities`), es a csoport-szuro NEM akadalyozza - a hazigazda
     * lehet egy masik csoportban. A kuldo azonositojat a LiveKit kezbesitese adja.
     */
    z.object({ type: z.literal('breakout.help'), from: z.string(), group: z.number().int() }),
    /**
     * KERES egy resztvevotol: ossza meg a kepernyojet, vagy kapcsolja vissza a
     * mikrofonjat. Csak neki megy (`destinationIdentities`), es o dont - Igen
     * vagy Nem. A kero kilete NEM ebbol a keretbol jon, hanem abbol, akitol a
     * LiveKit kezbesitette: igy senki nem kerhet mas neveben.
     */
    z.object({
        type: z.literal('request'),
        id: z.string().max(40),
        kind: z.enum(['screen', 'unmute']),
    }),
    /** Valasz egy kerésre - csak a kerőnek megy. */
    z.object({
        type: z.literal('request.answer'),
        id: z.string().max(40),
        /**
         * `accepted` / `declined`: a cimzett dontott. `unsupported`: az eszkoze
         * nem tud kepernyot megosztani (telefonos bongeszo). `expired`: nem
         * valaszolt idoben.
         */
        answer: z.enum(['accepted', 'declined', 'unsupported', 'expired']),
    }),
]);
/** Rajz-szinek. Halk paletta - a megosztott kepernyo a fontos, nem a firka. */
export const DRAW_COLORS = [
    'oklch(62% 0.19 25)',
    'oklch(68% 0.16 85)',
    'oklch(62% 0.15 155)',
    'oklch(60% 0.16 250)',
    'oklch(60% 0.18 310)',
];
// ─── Vendeglink (H2) ──────────────────────────────────────────────────────────
/** Meddig ervenyes a link - orakban. */
export const GUEST_LINK_DURATIONS = [2, 24, 168];
export const createGuestLinkInput = z.object({
    permissions: z.array(guestPermissionSchema).default(ALL_GUEST_PERMISSIONS),
    expiresInHours: z.union([z.literal(2), z.literal(24), z.literal(168)]).default(2),
});
export const guestLinkSchema = z.object({
    id: z.string(),
    channelId: z.string(),
    permissions: z.array(guestPermissionSchema),
    createdBy: z.string().nullable(),
    createdAt: z.string(),
    expiresAt: z.string(),
    /** CSAK letrehozaskor jon vissza - kesobb nem allithato elo (csak a lenyomata van meg). */
    url: z.string().optional(),
});
/** Amit a vendeg a link megnyitasakor lat. */
export const guestLinkInfoSchema = z.object({
    valid: z.boolean(),
    /** Miert nem: lejart, visszavontak, nincs ilyen. */
    reason: z.enum(['expired', 'revoked', 'unknown']).optional(),
    /** Kinek a hivasa - a csatorna (vagy DM) neve, ahogy egy kulso felnek mutathato. */
    title: z.string().optional(),
    /** Keszulhet-e atirat (a csatorna/cég beallitasa szerint) - belepes elott elmondjuk. */
    mayTranscribe: z.boolean().optional(),
});
export const guestJoinInput = z.object({
    name: z.string().trim().min(2, 'Add meg a neved.').max(80, 'A név legfeljebb 80 karakter.'),
});
/** A varakozo vendeg allapota - ezt kerdezi a vendeg oldala. */
export const guestStatusSchema = z.object({
    state: huddleGuestStateSchema,
    permissions: z.array(guestPermissionSchema),
    /** Van-e eppen elo hivas a csatornaban (ha nincs, a varakozo ezt latja). */
    huddleLive: z.boolean(),
    /**
     * Keszul-e atirat (H2): ha igen, a vendeg bongeszoje a SAJAT hangjat
     * rogziti es tolti fel - ugyanugy, mint minden kolléga. Igy egy kolléga
     * tavozasa nem szakitja meg a vendeg atiratat.
     */
    transcribe: z.boolean().default(false),
    /** A hivas kezdete (ISO): az atirat-darabok a hivas idovonalara kerulnek. */
    huddleStartedAt: z.string().nullable().default(null),
    /** Beengedes utan: a csatlakozasi jegy. */
    token: huddleTokenSchema.optional(),
    /**
     * A NYITOTT kiscsoportos idoszak (docs/19), ha a vendeg a hivasban van. A vendegnek nincs
     * WebSocketje: a beosztast, a lejaratot es a szerver idejet innen kapja.
     */
    breakout: huddleBreakoutSchema.nullable().default(null),
});
export const guestPermissionsInput = z.object({
    permissions: z.array(guestPermissionSchema),
});
//# sourceMappingURL=huddle.js.map